import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { plantRefsService } from "../services/plant-refs.service";
import { plantingsService } from "../services/plantings.service";
import type {
	CreatePlantingRequest,
	ErrorResponse,
	PlantingGeoJSONParams,
	PlantingListParams,
	UpdatePlantingRequest,
} from "../types";

export const plantingKeys = {
	all: ["plantings"] as const,
	lists: () => [...plantingKeys.all, "list"] as const,
	list: (params?: PlantingListParams) => [...plantingKeys.lists(), params] as const,
	geojson: (params?: PlantingGeoJSONParams) => [...plantingKeys.all, "geojson", params] as const,
	details: () => [...plantingKeys.all, "detail"] as const,
	detail: (id: string) => [...plantingKeys.details(), id] as const,
};

export const plantRefKeys = {
	plants: (organizationId: string) => ["plant-refs", "plants", organizationId] as const,
	contracts: (organizationId: string) => ["plant-refs", "contracts", organizationId] as const,
};

export function usePlantingsList(params?: PlantingListParams) {
	return useQuery({
		queryKey: plantingKeys.list(params),
		queryFn: () => plantingsService.list(params),
		enabled: !!params?.organizationId,
	});
}

export function usePlantingsGeoJSON(
	params?: PlantingGeoJSONParams,
	options?: { enabled?: boolean }
) {
	return useQuery({
		queryKey: plantingKeys.geojson(params),
		queryFn: () => plantingsService.getGeoJSON(params),
		enabled: !!params?.organizationId && (options?.enabled ?? true),
	});
}

export function usePlanting(id: string) {
	return useQuery({
		queryKey: plantingKeys.detail(id),
		queryFn: () => plantingsService.getById(id),
		enabled: !!id,
	});
}

export function usePlants(organizationId: string) {
	return useQuery({
		queryKey: plantRefKeys.plants(organizationId),
		queryFn: () => plantRefsService.listPlants(organizationId),
		enabled: !!organizationId,
	});
}

export function usePlantContracts(organizationId: string) {
	return useQuery({
		queryKey: plantRefKeys.contracts(organizationId),
		queryFn: () => plantRefsService.listContracts(organizationId),
		enabled: !!organizationId,
	});
}

export function useCreatePlanting() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreatePlantingRequest) => plantingsService.create(data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: plantingKeys.lists() });
			queryClient.invalidateQueries({
				queryKey: plantingKeys.geojson({ organizationId: variables.organizationId }),
			});
			message.success("Ekin qo'shildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Ekin qo'shishda xatolik";
			message.error(msg);
		},
	});
}

export function useUpdatePlanting() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdatePlantingRequest }) =>
			plantingsService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: plantingKeys.lists() });
			queryClient.invalidateQueries({ queryKey: plantingKeys.all });
			queryClient.invalidateQueries({ queryKey: plantingKeys.detail(variables.id) });
			message.success("Ekin yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Ekin yangilashda xatolik";
			message.error(msg);
		},
	});
}

export function useDeletePlanting() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => plantingsService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: plantingKeys.lists() });
			queryClient.invalidateQueries({ queryKey: plantingKeys.all });
			message.success("Ekin o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Ekin o'chirishda xatolik";
			message.error(msg);
		},
	});
}
